import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Plus, Trash2, GripVertical, Save, Sparkles, Loader2, Target, Activity,
    ChevronDown, ChevronUp, Download,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";

const PRIORITY_OPTS = [
    { value: "alta", label: "Alta", color: "bg-[#FBE3DC] text-[#8B2E1F] border-[#F0BFB3]" },
    { value: "media", label: "Média", color: "bg-[#FBF4E5] text-[#8A6A15] border-[#EDD9A6]" },
    { value: "baixa", label: "Baixa", color: "bg-[#E5EFE0] text-[#315B3A] border-[#BFD5B7]" },
];
const INTERVENTION_TYPE_OPTS = [
    { value: "farmacologica", label: "Farmacológica" },
    { value: "nao_farmacologica", label: "Não farmacológica" },
    { value: "exame", label: "Exame" },
    { value: "orientacao", label: "Orientação" },
    { value: "encaminhamento", label: "Encaminhamento" },
];

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `id-${Math.random().toString(36).slice(2, 10)}`);

const newGoal = () => ({ _id: uid(), title: "", deadline: "30 dias" });
const newIntervention = () => ({ _id: uid(), title: "", type: "nao_farmacologica", responsible: "" });
const newProblem = () => ({
    _id: uid(), title: "", description: "", priority: "media",
    goals: [newGoal()], interventions: [newIntervention()],
});

// Add _id and default fields if missing (when hydrating from server)
const hydrate = (list) => (list || []).map((p) => ({
    _id: p._id || uid(),
    title: p.title || "",
    description: p.description || "",
    priority: p.priority || "media",
    goals: (p.goals || []).map((g) => ({ _id: g._id || uid(), title: g.title || "", deadline: g.deadline || "" })),
    interventions: (p.interventions || []).map((i) => ({
        _id: i._id || uid(), title: i.title || "", type: i.type || "nao_farmacologica", responsible: i.responsible || "",
    })),
}));

// Strip client-only _id before persisting.
const strip = (problems) => problems.map((p) => ({
    title: p.title, description: p.description, priority: p.priority,
    goals: p.goals.map((g) => ({ title: g.title, deadline: g.deadline })),
    interventions: p.interventions.map((i) => ({ title: i.title, type: i.type, responsible: i.responsible })),
}));

export default function PlanEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [problems, setProblems] = useState([]);
    const [patient, setPatient] = useState(null);
    const [hasSummary, setHasSummary] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/consultations/${id}`);
                setPatient(data.patient);
                setProblems(hydrate(data.care_plan?.problems || []));
                setHasSummary(!!data.care_plan?.summary);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [id]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const onDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setProblems((items) => {
            const oldIdx = items.findIndex((p) => p._id === active.id);
            const newIdx = items.findIndex((p) => p._id === over.id);
            return arrayMove(items, oldIdx, newIdx);
        });
    }, []);

    const updateProblem = (pid, patch) => setProblems((prev) => prev.map((p) => (p._id === pid ? { ...p, ...patch } : p)));
    const removeProblem = (pid) => setProblems((prev) => prev.filter((p) => p._id !== pid));
    const addProblem = () => setProblems((prev) => [...prev, newProblem()]);

    const importFromAI = async () => {
        setImporting(true);
        try {
            const { data } = await api.post("/care-plans/extract-structured", { consultation_id: id });
            const parsed = hydrate(data.problems);
            if (!parsed.length) {
                toast.error("A IA não conseguiu identificar problemas estruturados. Edite manualmente.");
                return;
            }
            if (problems.length && !window.confirm(`Já existem ${problems.length} problema(s). Substituir tudo pela versão da IA?`)) return;
            setProblems(parsed);
            toast.success(`${parsed.length} problema(s) importado(s) da IA. Revise antes de salvar.`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setImporting(false); }
    };

    const save = async () => {
        // Validate: at least title on every problem
        const invalid = problems.some((p) => !p.title.trim());
        if (invalid) { toast.error("Todo problema precisa de um título."); return; }
        setSaving(true);
        try {
            await api.post("/care-plans", { consultation_id: id, problems: strip(problems) });
            toast.success("Plano estruturado salvo.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const downloadPdf = async () => {
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/care-plans/consultation/${id}/pdf`, { credentials: "include" });
            if (!resp.ok) throw new Error("Falha ao gerar PDF");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `plano_estruturado_${id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(e.message || "Erro"); }
    };

    if (loading) return <AppShell><div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-brand" size={32} /></div></AppShell>;

    return (
        <AppShell>
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <button onClick={() => navigate(`/consultations/${id}`)} className="btn-ghost mb-3 text-sm" data-testid="plan-editor-back">
                        <ArrowLeft size={14} /> Voltar à consulta
                    </button>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">Plano de cuidado estruturado</div>
                    <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">
                        {patient?.full_name || "Paciente"}
                    </h1>
                    <p className="text-sm text-muted-clinical mt-1">
                        Arraste os problemas para reordenar. Cada problema pode ter várias metas SMART e intervenções.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button data-testid="plan-editor-import-ai" onClick={importFromAI} disabled={importing || !hasSummary} className="btn-secondary" title={!hasSummary ? "Gere primeiro um plano com IA na aba Plano da consulta" : ""}>
                        {importing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Importar do plano da IA
                    </button>
                    <button data-testid="plan-editor-add-problem" onClick={addProblem} className="btn-secondary">
                        <Plus size={14} /> Novo problema
                    </button>
                    <button data-testid="plan-editor-save" onClick={save} disabled={saving} className="btn-primary">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
                    </button>
                    <button data-testid="plan-editor-pdf" onClick={downloadPdf} className="btn-secondary">
                        <Download size={14} /> PDF
                    </button>
                </div>
            </div>

            {problems.length === 0 ? (
                <div className="card-earthy text-center py-14" data-testid="plan-editor-empty">
                    <div className="w-14 h-14 rounded-2xl bg-alt flex items-center justify-center mx-auto mb-4 text-brand"><Target size={26} /></div>
                    <h3 className="font-display text-xl text-brand">Nenhum problema ainda</h3>
                    <p className="text-sm text-muted-clinical mt-1 mb-5">Comece do zero ou importe do plano gerado pela IA.</p>
                    <div className="flex gap-2 justify-center">
                        <button onClick={addProblem} className="btn-primary"><Plus size={14} /> Adicionar problema</button>
                        <button onClick={importFromAI} disabled={importing || !hasSummary} className="btn-secondary"><Sparkles size={14} /> Importar da IA</button>
                    </div>
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={problems.map((p) => p._id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-4">
                            {problems.map((p, idx) => (
                                <SortableProblem
                                    key={p._id}
                                    problem={p}
                                    index={idx}
                                    onChange={(patch) => updateProblem(p._id, patch)}
                                    onRemove={() => removeProblem(p._id)}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </AppShell>
    );
}

function SortableProblem({ problem, index, onChange, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: problem._id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
    };
    const [expanded, setExpanded] = useState(true);
    const prio = PRIORITY_OPTS.find((o) => o.value === problem.priority) || PRIORITY_OPTS[1];

    const addGoal = () => onChange({ goals: [...problem.goals, newGoal()] });
    const updGoal = (gid, patch) => onChange({ goals: problem.goals.map((g) => (g._id === gid ? { ...g, ...patch } : g)) });
    const rmGoal = (gid) => onChange({ goals: problem.goals.filter((g) => g._id !== gid) });

    const addIv = () => onChange({ interventions: [...problem.interventions, newIntervention()] });
    const updIv = (iid, patch) => onChange({ interventions: problem.interventions.map((i) => (i._id === iid ? { ...i, ...patch } : i)) });
    const rmIv = (iid) => onChange({ interventions: problem.interventions.filter((i) => i._id !== iid) });

    return (
        <div ref={setNodeRef} style={style} data-testid={`problem-card-${index}`} className={`card-earthy border-l-4 ${prio.color.split(" ").pop()}`}>
            <div className="flex items-start gap-3">
                <button
                    {...attributes}
                    {...listeners}
                    className="mt-2 cursor-grab active:cursor-grabbing text-muted-clinical hover:text-brand touch-none shrink-0"
                    data-testid={`problem-drag-${index}`}
                    aria-label="Arrastar problema"
                >
                    <GripVertical size={18} />
                </button>
                <span className="w-7 h-7 mt-1 rounded-full bg-brand text-white flex items-center justify-center font-display text-sm font-semibold shrink-0">
                    {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_160px_auto_auto] gap-2 items-center">
                        <input
                            data-testid={`problem-title-${index}`}
                            value={problem.title}
                            onChange={(e) => onChange({ title: e.target.value })}
                            placeholder="Título do problema (ex: Polifarmácia com risco Beers)"
                            className="input-clinical font-medium text-brand min-w-0"
                        />
                        <select
                            data-testid={`problem-priority-${index}`}
                            value={problem.priority}
                            onChange={(e) => onChange({ priority: e.target.value })}
                            className={`input-clinical text-sm ${prio.color}`}
                        >
                            {PRIORITY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <button onClick={() => setExpanded((x) => !x)} className="btn-ghost text-sm p-2 justify-self-end" data-testid={`problem-toggle-${index}`}>
                            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <button onClick={onRemove} className="p-2 text-[var(--status-danger)] hover:bg-[var(--status-danger-bg)] rounded justify-self-end" data-testid={`problem-remove-${index}`}>
                            <Trash2 size={16} />
                        </button>
                    </div>
                    {expanded && (
                        <div className="mt-4 space-y-4">
                            <textarea
                                data-testid={`problem-description-${index}`}
                                value={problem.description}
                                onChange={(e) => onChange({ description: e.target.value })}
                                placeholder="Contexto clínico do problema"
                                rows={2}
                                className="input-clinical text-sm"
                            />

                            {/* Goals */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 text-brand-secondary text-sm font-medium"><Target size={14} /> Metas SMART</div>
                                    <button onClick={addGoal} className="btn-ghost text-xs" data-testid={`goal-add-${index}`}><Plus size={12} /> Nova meta</button>
                                </div>
                                {problem.goals.length === 0 ? (
                                    <p className="text-xs text-muted-clinical italic">Sem metas cadastradas.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {problem.goals.map((g, gi) => (
                                            <div key={g._id} className="grid grid-cols-1 md:grid-cols-[1fr_160px_32px] gap-2 items-center bg-[rgba(30,63,51,0.04)] rounded-lg p-2">
                                                <input
                                                    data-testid={`goal-title-${index}-${gi}`}
                                                    value={g.title}
                                                    onChange={(e) => updGoal(g._id, { title: e.target.value })}
                                                    placeholder="Ex: Reduzir polifarmácia em 2 fármacos"
                                                    className="input-clinical text-sm min-w-0"
                                                />
                                                <input
                                                    data-testid={`goal-deadline-${index}-${gi}`}
                                                    value={g.deadline}
                                                    onChange={(e) => updGoal(g._id, { deadline: e.target.value })}
                                                    placeholder="Prazo"
                                                    className="input-clinical text-sm"
                                                />
                                                <button onClick={() => rmGoal(g._id)} className="p-1.5 text-muted-clinical hover:text-[var(--status-danger)] justify-self-end" data-testid={`goal-remove-${index}-${gi}`}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Interventions */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 text-brand-secondary text-sm font-medium"><Activity size={14} /> Intervenções</div>
                                    <button onClick={addIv} className="btn-ghost text-xs" data-testid={`iv-add-${index}`}><Plus size={12} /> Nova intervenção</button>
                                </div>
                                {problem.interventions.length === 0 ? (
                                    <p className="text-xs text-muted-clinical italic">Sem intervenções cadastradas.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {problem.interventions.map((iv, ii) => (
                                            <div key={iv._id} className="grid grid-cols-1 md:grid-cols-[1fr_180px_160px_32px] gap-2 bg-[rgba(30,63,51,0.04)] rounded-lg p-2 items-center">
                                                <input
                                                    data-testid={`iv-title-${index}-${ii}`}
                                                    value={iv.title}
                                                    onChange={(e) => updIv(iv._id, { title: e.target.value })}
                                                    placeholder="Ex: Iniciar fisioterapia funcional 2x/semana"
                                                    className="input-clinical text-sm min-w-0"
                                                />
                                                <select
                                                    data-testid={`iv-type-${index}-${ii}`}
                                                    value={iv.type}
                                                    onChange={(e) => updIv(iv._id, { type: e.target.value })}
                                                    className="input-clinical text-sm"
                                                >
                                                    {INTERVENTION_TYPE_OPTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                </select>
                                                <input
                                                    data-testid={`iv-responsible-${index}-${ii}`}
                                                    value={iv.responsible}
                                                    onChange={(e) => updIv(iv._id, { responsible: e.target.value })}
                                                    placeholder="Responsável"
                                                    className="input-clinical text-sm"
                                                />
                                                <button onClick={() => rmIv(iv._id)} className="p-1.5 text-muted-clinical hover:text-[var(--status-danger)] justify-self-end" data-testid={`iv-remove-${index}-${ii}`}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
